import { Link } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";
import { Block, LegalShell, Points } from "./Legal";
import { WebDeleteAccount } from "../components/sections/WebDeleteAccount";

const linkCls = "font-medium text-teal-deep underline-offset-2 hover:underline";

// Google Play "Data safety" deletion page: the exact steps, what's erased,
// what's kept and for how long. Mirrors the app's own delete screen and the
// app-delete-account edge function, so all three tell the same story.
export default function DeleteAccount() {
  usePageMeta({
    title: "Delete your FitBuddy account and data | FitBuddy",
    description:
      "How to delete your FitBuddy account and all of its data, in the app or right here on the web. See exactly what is erased, what is kept and for how long.",
    path: "/delete-account",
  });
  return (
    <LegalShell
      title="Delete your account and data"
      intro="You can delete your FitBuddy account and everything in it whenever you like, in the app or right here on the web. It's instant and no one needs to approve it. This applies to the FitBuddy app by FitBuddy on Google Play and the App Store."
      updated="9 October 2026"
    >
      <Block heading="Option 1: delete it in the app">
        <ol className="list-decimal space-y-2 pl-5 marker:font-bold marker:text-navy">
          <li>Open the FitBuddy app and sign in.</li>
          <li>Go to Settings.</li>
          <li>
            Scroll to the bottom and tap <strong>Delete account &amp; data</strong>.
          </li>
          <li>Read the summary, type DELETE to unlock the button, then confirm.</li>
        </ol>
        <p>Your account is erased straight away and you're signed out.</p>
      </Block>

      <Block heading="Option 2: delete it here, without the app">
        <p>
          Removed the app already? Prove the account is yours with a code to
          your phone (or your email and password, if that's how you sign in),
          then delete it below.
        </p>
        <div className="pt-2">
          <WebDeleteAccount />
        </div>
      </Block>

      <Block heading="What gets deleted">
        <Points
          items={[
            "Your account and sign-in details (phone number, or email and password).",
            "Your profile: name, sex, age, height, goals, units and settings.",
            "Your profile picture and any progress photos attached to weigh-ins.",
            "Every weigh-in, body measurement and body-fat estimate.",
            "All food and drink logs, saved recipes, diary days and energy overrides.",
            "All workouts, sets and exercise logs.",
            "Your AI coach conversations and anything you attached to them.",
            "Your WHOOP connection and the activity data it synced.",
            "Your calorie bank and streaks (they're worked out from your entries, so they go with them).",
            "Friendships, friend requests, group memberships, group posts, reactions and your shareable card.",
            "Referral and voucher records linked to your account.",
          ]}
        />
      </Block>

      <Block heading="What's kept, and for how long">
        <Points
          items={[
            "Community groups you created stay up for their members, with your name removed from them.",
            "Payment and invoice records held by Stripe for any shop orders are kept for as long as tax and accounting law requires, typically up to 6 years. FitBuddy never holds your card details.",
            "Encrypted backups kept by our hosting provider roll off automatically within 30 days. They aren't used to restore deleted accounts.",
            "Messages you sent through the website contact form aren't linked to your account. Ask through the same form if you'd like those removed too.",
          ]}
        />
      </Block>

      <Block heading="Just want a fresh start?">
        <p>
          If you'd rather keep your account, Settings in the app has{" "}
          <strong>Reset all my data</strong>, which clears your logs and history
          but leaves your sign-in in place. More on how we handle your data is in
          the{" "}
          <Link to="/privacy" className={linkCls}>
            Privacy Policy
          </Link>
          , and help with anything else is on the{" "}
          <Link to="/support" className={linkCls}>
            support page
          </Link>
          .
        </p>
      </Block>
    </LegalShell>
  );
}
